// eslint-disable-next-line import/no-named-as-default
import { environment } from '@environments';
import moment from 'moment';
import store from '@/store';
import { pickBy } from 'lodash';
import i18n from '@/plugins/i18n';
import { getFilled } from '@/common/helpers/getFilled';
import { helpers } from '@/common/helpers';

export default (Vue) => {
  Vue.prototype.$eventBus = new Vue();

  Vue.prototype.$moment = moment;

  Vue.prototype.$openDialog = params => {
    Vue.prototype.$eventBus.$emit('open-dialog', params);

    return new Promise((resolve, reject) => {
      Vue.prototype.$eventBus.$on(params.component, response => {
        Vue.prototype.$eventBus.$off(params.component);
        if (response.success) {
          resolve({ popupName: params.component, data: response.data });
        } else if (params.returnReject || (!response.seccess && response.data)) {
          reject(params.component, response.data);
        }
      });
    });
  };

  Vue.prototype.$closeDialog = componentName => Vue.prototype.$eventBus.$emit('close-dialog', componentName);

  Vue.prototype.$notifySuccess = (message = i18n.t('snotify.successTitle')) => {
    Vue.prototype.$eventBus.$emit('notify', { type: 'success', message });
  };

  Vue.prototype.$notifyError = (message = i18n.t('snotify.errorTitle')) => {
    Vue.prototype.$eventBus.$emit('notify', { type: 'error', message });
  };

  Vue.prototype.$notifyWarn = (message = i18n.t('snotify.errorTitle')) => {
    Vue.prototype.$eventBus.$emit('notify', { type: 'warning', message });
  };

  Vue.prototype.$notifyInfo = (message = i18n.t('snotify.infoTitle')) => {
    Vue.prototype.$eventBus.$emit('notify', { type: 'info', message });
  };

  Vue.prototype.$errorDecode = (err, message = '') => helpers.errorDecoder(err, message);

  Vue.prototype.$warnMessageHandler = err => {
    helpers.warnMessageHandler(err);
  };

  Vue.prototype.$errorMessageHandler = err => {
    helpers.errorMessageHandler(err);
  };

  Vue.prototype.$getTimezone = (offset = null) => {
    const currentOffset = offset || Vue.prototype.$moment().utcOffset() / 60;

    if (currentOffset === 0) return 'UTC';
    const timezone = currentOffset > 0 ? `UTC+${currentOffset}` : `UTC${currentOffset}`;

    // correct offset for backend
    return timezone.replace(/\.5/, ':30').replace(/\.75/, ':45');
  };

  Vue.prototype.$timezone = (() => Vue.prototype.$getTimezone())();

  Vue.prototype.$isLoggedNoSession = () => (localStorage.getItem('token') || localStorage.getItem('refreshToken')) && !sessionStorage.getItem('isLoggedIn');

  Vue.prototype.$getCurrency = () => {
    const currencyId = store.state.loggedAccount.accountClinic && store.state.loggedAccount.accountClinic.currency && store.state.loggedAccount.accountClinic.id;
    const entity = store.state.loggedAccount.currencies && store.state.loggedAccount.currencies.find(c => c.id === currencyId);
    return entity ? entity.sign : '$';
  };

  Vue.prototype.$getProviderTypesFromPermissions = (permissions) => {
    if (!permissions) return;
    const typeMapper = [
      {
        permission: 'take-part:primary-care-appointment',
        type: i18n.t('constants.bookingType.primary'),
      },
      {
        permission: 'take-part:urgent-care-appointment',
        type: i18n.t('constants.bookingType.urgent'),
      },
    ];

    const names = permissions.filter(item => typeMapper.map(item => item.permission).includes(item.name)).map(item => item.name);

    const mapped = typeMapper.filter(item => names.includes(item.permission)).map(item => item.type);
    return [...new Set(mapped)].join(', ');
  };

  Vue.prototype.$getDayTime = () => {
    const hours = Vue.prototype.$moment().local().format('H');

    if (hours <= 17 && hours >= 12) {
      return 'afternoon';
    } if (hours > 17) {
      return 'evening';
    } if (hours >= 0 && hours <= 12) {
      return 'morning';
    } return 'hi';
  };

  Vue.prototype.$cleanPayload = (obj) => pickBy(obj, (value) => {
    if (Array.isArray(value)) {
      return !!(value && value.length);
    }
    if (typeof value === 'boolean') {
      return true;
    }

    return !!value || value === 0;
  });

  Vue.prototype.$recaptcha = {
    executePromise: null,
  };

  Vue.prototype.$clinicHasFeature = (clinic, feature) => !!(clinic && clinic.features.find(el => el.name === feature));
  Vue.prototype.$accountHasPermission = (permission) => {
    const permissions = store.state.loggedAccount.account && store.state.loggedAccount.account.permissions;
    return !!(permissions && permissions.find(el => el.name === permission));
  };
  Vue.prototype.$sanitize = (value) => {
    if (value && value.length && typeof value === 'string') {
      return value.replace(/(<([^>]+)>)/gi, '');
    }
    return value;
  };

  Vue.prototype.$getFilled = getFilled;

  Vue.prototype.$clearCookies = () => Vue.$cookies.keys().forEach(cookie => Vue.$cookies.remove(cookie));
  Vue.prototype.$setTabSession = (username = '') => Vue.$cookies.set('loggedIn', username || 'true', 0);
  Vue.prototype.$getTabSession = () => Vue.$cookies.get('loggedIn');
  Vue.prototype.$clearTabSession = () => Vue.$cookies.remove('loggedIn');

  Vue.prototype.$adaptPhoneNumberArabic = (phoneNumber, isRtl) => {
    if (phoneNumber?.includes('+')) {
      return isRtl ? `${phoneNumber.replace(/\+/g, '')}+` : phoneNumber;
    }

    return isRtl ? `${phoneNumber}+` : `+${phoneNumber}`;
  };

  Vue.prototype.$appendSideqik = () => {
    const script = document.createElement('script');
    script.innerText = `(function(w,d,s,u,n,t,p){w[n]=w[n]||function(){(w[n].q=w[n].q||[]).push(arguments)};t=d.createElement(s),p=d.getElementsByTagName(s)[0];t.id='sideqik-sdk';t.async=1;t.src=u;p.parentNode.insertBefore(t,p)})(window,document,'script','${environment.VUE_APP_SIDEQIK_LINK}#${environment.VUE_APP_SIDEQIK_HASH}','sideqik');`;
    document.head.appendChild(script);
  };

  Vue.prototype.sideqik = (eventType, data = {}) => {
    try {
      // eslint-disable-next-line no-undef
      sideqik?.('track', eventType, data);
    } catch (e) {
      console.log('Sideqik Error', e);
    }
  };

  Vue.prototype.$initPostAffiliatePro = () => {
    const papScriptHeader = document.createElement('script');
    papScriptHeader.src = `https://${environment.VUE_APP_POST_AFFILIATE_PRO_DOMAIN}/scripts/trackjs.js`;
    papScriptHeader.id = 'pap_x2s6df8d';
    papScriptHeader.type = 'text/javascript';
    papScriptHeader.onload = () => {
      try {
        // eslint-disable-next-line no-undef
        PostAffTracker.setAccountId('default1');
        // eslint-disable-next-line no-undef
        PostAffTracker.track();
      } catch (err) {
        console.log('Affiliate init error', err);
      }
    };
    document.body.appendChild(papScriptHeader);
  };

  Vue.prototype.$postAffiliateProBuy = async (options = {}) => {
    try {
      // eslint-disable-next-line no-undef
      const sale = PostAffTracker.createSale();
      sale.setTotalCost(options.price);
      sale.setOrderID(options.orderId);
      if (options.affiliateId) {
        sale.setAffiliateID(options.affiliateId);
      }
      sale.setProductID(options.productId);
      // eslint-disable-next-line no-undef
      PostAffTracker.register();
    } catch (err) {
      console.log('Affiliate sale error', err);
    }
  };

  const currentAffiliateProgram = environment.VUE_APP_AFFILIATE_PROGRAM;

  Vue.prototype.$affiliateProgramHandleSale = (options) => {
    if (!currentAffiliateProgram || !options) return;

    const affiliateSaleHandler = {
      PostAffiliatePro: Vue.prototype.$postAffiliateProBuy,
    };

    affiliateSaleHandler[currentAffiliateProgram]?.(options);
  };

  Vue.prototype.$initAffiliateProgram = () => {
    if (!currentAffiliateProgram) return;

    const affiliateInitializer = {
      PostAffiliatePro: Vue.prototype.$initPostAffiliatePro,
      Sideqik: Vue.prototype.$appendSideqik,
    };

    affiliateInitializer[currentAffiliateProgram]?.();
  };
};
